<template>
  <h3>{{ $t("user.loginTitle") }}</h3>

  <SocialLoginButtons @loginSuccess="onSocialLoginSuccess" />

  <form class="form" @submit.prevent="onSubmit()" name="login">
    <CsrfField />
    <FormGroup :v="v$.email" label="label.email">
      <TextField
        v-model="formValue.email"
        type="text"
        inputmode="email"
        name="username"
        :disabled="props.saving"
        placeholder="placeholder.email"
        autocapitalize="off"
        autocomplete="username"
      />
    </FormGroup>
    <FormGroup :v="v$.password" label="label.password">
      <PasswordField
        v-model="formValue.password"
        name="password"
        :disabled="props.saving"
        autocapitalize="off"
        autocomplete="current-password"
      />
    </FormGroup>
    <div class="form__actions">
      <AppButton type="submit" :loading="props.saving">
        {{ $t("user.login") }}
      </AppButton>
    </div>
    <slot />
  </form>
</template>

<script setup lang="ts">
import AppButton from "@/components/app-button.vue";
import FormGroup from "@/components/form/form-group.vue";
import TextField from "@/components/form/text-field.vue";
import CsrfField from "@/components/form/csrf-field.vue";
import PasswordField from "@/components/form/password-field.vue";
import SocialLoginButtons from "./social-login/social-login-buttons.vue";
import { computed, reactive, watch } from "vue";
import { email, required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { UserLoginData } from "../../types";

const props = defineProps<{
  saving?: boolean;
  errors?: Record<string, string[]>;
}>();

const emit = defineEmits<{
  (e: "submit", v: UserLoginData): void;
  (e: "socialLoginSuccess", payload: { action: "login" | "register" }): void;
}>();

const formValue = reactive<UserLoginData>({
  email: "",
  password: "",
});

const rules = computed(() => ({
  email: { required, email },
  password: { required },
}));

const v$ = useVuelidate(rules, formValue, {
  $externalResults: computed(() => props.errors) as any,
});

watch(
  () => formValue.password,
  () => {
    // clear email validation errors (login failed), otherwise form will stay invalid and unsubmitable
    if (v$.value.email.$externalResults.length > 0) {
      v$.value.email.$model = formValue.email;
    }
  }
);

const onSubmit = () => {
  v$.value.$touch();

  if (!v$.value.$invalid) {
    emit("submit", { ...formValue });
  }
};

const onSocialLoginSuccess = (payload: { action: "login" | "register" }) => {
  emit("socialLoginSuccess", payload);
};
</script>
